import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import CallSessionList from '../components/CallSessionList';
import SalesProfileWizard from '../components/SalesProfileWizard';
import BetaFeedbackModal from '../components/BetaFeedbackModal';
import '../App.css';

import PersonaImages from '../utils/PersonaImages';

const load_templates = async (token, set_templates, set_loading_templates, set_error) => {
	set_loading_templates(true);
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const url = Constants.SERVER_URL + '/api/call/templates';
		const response = await axios.get(url, { headers });
		set_templates(response.data.templates || []);
	} catch (error) {
		set_error('Failed to load agents.');
	} finally {
		set_loading_templates(false);
	}
};

const load_quota = async (token, set_quota) => {
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const response = await axios.get(Constants.SERVER_URL + '/api/quota', { headers });
		set_quota(response.data);
	} catch (error) {
		// quota fetch failure is non-fatal - allow navigation
	}
};

const render_agent_card = (template, quota_allowed, on_quota_exceeded, on_select_template, on_preview_persona) => {
	const img = PersonaImages[template.business_type];
	return (
		<Card
			key={template.id}
			className={'calls-agent-card' + (!quota_allowed ? ' calls-agent-card-disabled' : '')}
			onClick={() => {
				if(!quota_allowed) {
					on_quota_exceeded();
				} else {
					on_select_template(template);
				}
			}}>
			<Card.Body className="calls-agent-card-body">
				{img && (
					<img
						src={img}
						alt={template.prospect_name}
						className="calls-agent-card-avatar"
						onClick={(e) => {
							e.stopPropagation();
							on_preview_persona(template);
						}}
						style={{ cursor: 'zoom-in' }}
					/>
				)}
				<div className="calls-agent-card-business">{template.prospect_name}</div>
				<div className="calls-agent-card-person">{template.business_type}</div>
				<div className="calls-agent-card-divider" />
				<div className="calls-agent-card-trait">{template.personality_trait}</div>
			</Card.Body>
		</Card>
	);
};

const render_agents_section = (templates, loading_templates, quota_allowed, on_quota_exceeded, on_select_template, on_preview_persona) => {
	return (
		
		<React.Fragment>
			<h4 className="calls-title">Start a new call</h4>
			<p className="sub-title-text"> Pick a persona to begin</p>
			<div className="calls-agents-section">
				
				{loading_templates ? (
					<div className="call-loading"><Spinner animation="border" size="sm" /></div>
				) : (
					<div className="calls-agents-grid">
						{templates.map(template => render_agent_card(template, quota_allowed, on_quota_exceeded, on_select_template, on_preview_persona))}
					</div>
				)}
			</div>
		</React.Fragment>
	);
};

const render_persona_info_row = (label, value) => {
	if(!value) return null;
	return (
		<div className="persona-modal-section" key={label}>
			<div className="persona-modal-section-label">{label}</div>
			<div className="persona-modal-section-value">{value}</div>
		</div>
	);
};

function CallsView() {
	const { token } = useAuth();
	const navigate = useNavigate();

	const [templates, set_templates] = useState([]);
	const [loading_templates, set_loading_templates] = useState(false);
	const [starting_session, set_starting_session] = useState(false);
	const [error, set_error] = useState('');
	const [quota, set_quota] = useState(null);
	const [show_upgrade_modal, set_show_upgrade_modal] = useState(false);
	const [show_beta_modal, set_show_beta_modal] = useState(false);
	const [show_wizard, set_show_wizard] = useState(false);
	const [wizard_template, set_wizard_template] = useState(null);
	const [persona_preview, set_persona_preview] = useState(null);

	useEffect(() => {
		load_templates(token, set_templates, set_loading_templates, set_error);
		load_quota(token, set_quota);
	}, [token]);

	useEffect(function() {
		const user_id = localStorage.getItem(Constants.USER_ID);
		if(!user_id || !token) {
			return;
		}

		const shown_key = 'beta_feedback_modal_shown_' + user_id;
		if(localStorage.getItem(shown_key)) {
			return;
		}

		const headers = { Authorization: 'Bearer ' + token };

		Promise.allSettled([
			axios.get(Constants.SERVER_URL + '/api/quota', { headers }),
			axios.get(Constants.SERVER_URL + '/api/user_subscription', { headers }),
		]).then(function(results) {
			const quota_result = results[0];
			const sub_result = results[1];

			const quota_exhausted = (quota_result.status === 'fulfilled') && (quota_result.value.data) && (quota_result.value.data.allowed === false);
			const is_beta = (sub_result.status === 'fulfilled') && (sub_result.value.data) && (sub_result.value.data.plan_name === 'Beta');

			if(is_beta && quota_exhausted) {
				set_show_beta_modal(true);
			}
		});
	}, [token]);

	const quota_allowed = quota === null || quota.allowed;

	const handle_select_template = (template) => {
		set_wizard_template(template);
		set_show_wizard(true);
	};

	const create_call_session = async (sales_profile, difficulty, settings) => {
		const voice_mode = localStorage.getItem(Constants.DEFAULT_VOICE_MODE) !== 'false';
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const body = {
			template_id: wizard_template.id,
			sales_profile_id: sales_profile ? sales_profile.id : null,
			difficulty,
			voice_mode,
		};
		if(settings) {
			body.required_3a_successes = settings.required_3a_successes;
			body.patience_multiplier = settings.patience_multiplier;
			body.objection_strength = settings.objection_strength;
			body.min_exchanges = settings.min_exchanges;
		}
		const response = await axios.post(
			Constants.SERVER_URL + '/api/call/session',
			body,
			{ headers }
		);
		return response.data;
	};

	const handle_wizard_complete = async (sales_profile, difficulty, settings) => {
		set_show_wizard(false);
		set_starting_session(true);
		try {
			const { session_id, audio_base64, audio_format } = await create_call_session(sales_profile, difficulty, settings);
			navigate('/call/' + session_id, {
				state: {
					template: wizard_template,
					sales_profile,
					audio_base64,
					audio_format,
				}
			});
		} catch (error) {
			set_starting_session(false);
			if(error.response && error.response.status === 403) {
				set_error('Quota exhausted. Please upgrade your plan to continue training.');
			} else {
				set_error('Failed to start session.');
			}
		}
	};

	const handle_wizard_hide = () => {
		set_show_wizard(false);
		set_wizard_template(null);
	};

	const handle_beta_modal_close = function() {
		const user_id = localStorage.getItem(Constants.USER_ID);
		localStorage.setItem('beta_feedback_modal_shown_' + user_id, 'true');
		set_show_beta_modal(false);
	};

	const handle_quota_exceeded = () => {
		set_show_upgrade_modal(true);
	};

	const handle_upgrade_hide = () => {
		set_show_upgrade_modal(false);
	};

	const handle_upgrade_navigate = () => {
		navigate('/plans');
	};

	const handle_preview_persona = (template) => {
		set_persona_preview(template);
	};

	const handle_preview_hide = () => {
		set_persona_preview(null);
	};

	const preview_img = persona_preview ? PersonaImages[persona_preview.business_type] : null;

	const render_persona_preview_modal = () => {
		if(!persona_preview) {
			return null;
		}
		return (
			<Modal
				show={true}
				onHide={handle_preview_hide}
				centered
				dialogClassName="persona-modal-dialog">
				<Modal.Header closeButton>
					<Modal.Title>
						{persona_preview.prospect_name}
						<div className="persona-modal-header-business">{persona_preview.business_name || persona_preview.business_type}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="persona-modal-body">
					{preview_img && (
						<img
							src={preview_img}
							alt={persona_preview.prospect_name}
							className="persona-modal-image"
						/>
					)}
					<div className="persona-modal-info">
						{render_persona_info_row('Business Type', persona_preview.business_type)}
						{render_persona_info_row('Personality', persona_preview.personality_trait)}
						{render_persona_info_row('Scenario', persona_preview.scenario)}
						{render_persona_info_row('Pain Point', persona_preview.pain_point)}
						{render_persona_info_row('Beliefs', persona_preview.beliefs)}
					</div>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button variant="primary" onClick={handle_preview_hide}>Ok</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_upgrade_modal = () => {
		return (
			<Modal show={show_upgrade_modal} onHide={handle_upgrade_hide} centered>
				<Modal.Header closeButton>
					<Modal.Title>Training Credits Exhausted</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					You've used all your training credits for this month. <br/>
					Please upgrade your subscription to continue practicing.
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={handle_upgrade_hide}>Cancel</Button>
					<Button variant="primary" onClick={handle_upgrade_navigate}>Upgrade Plan</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const render_starting_modal = () => {
		return (
			<Modal show={starting_session} centered>
				<Modal.Body className="calls-starting-modal-body">
					<Spinner animation="border" variant="success" />
					<span className="calls-starting-modal-text">Starting call...</span>
				</Modal.Body>
			</Modal>
		);
	};

	return (
		<React.Fragment>
			<main className="calls-main">
				{error ? <Alert variant="danger">{error}</Alert> : null}
				{render_agents_section(templates, loading_templates, quota_allowed, handle_quota_exceeded, handle_select_template, handle_preview_persona)}
				<CallSessionList />
			</main>

			<SalesProfileWizard
				show={show_wizard}
				on_hide={handle_wizard_hide}
				on_complete={handle_wizard_complete}
			/>

			{render_persona_preview_modal()}
			{render_upgrade_modal()}
			{render_starting_modal()}
			<BetaFeedbackModal show={show_beta_modal} on_hide={handle_beta_modal_close} />
		</React.Fragment>
	);
}

export default CallsView;
